import withStyles from "isomorphic-style-loader/withStyles";
import React, { useRef } from "react";
import styles from "../../styles.scss";
import { Button, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { useLoading } from "../../../../../../../../../hooks/useLoading";

interface ImageSelectionInputProps {
  label: React.ReactNode;
  image: string;
  setImage: (url: string) => void;
  onUpload: (file: File) => Promise<string>;
  defaultImage: string;
  altText?: string;
  maxWidth?: number | null;
  maxHeight?: number | null;
  minWidth?: number | null;
  minHeight?: number | null;
  accept?: string;
  onError?: (message: string) => void;
}

const ImageSelectionInput: React.FC<ImageSelectionInputProps> = ({
  label,
  image,
  setImage,
  onUpload,
  defaultImage,
  altText,
  maxWidth = null,
  maxHeight = null,
  minWidth = null,
  minHeight = null,
  accept = "image/png,image/jpeg,image/jpg,image/svg+xml",
  onError,
}) => {
  const reportError = (message: string) => {
    if (onError) {
      onError(message);
    } else {
      console.error(message);
    }
  };
  const fileInputRef = useRef(null);
  const { loading: isUploading, runWithLoading } = useLoading();

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const isIcoFile = (file: File) =>
    file.type === "image/x-icon" ||
    file.type === "image/vnd.microsoft.icon" ||
    file.name.toLowerCase().endsWith(".ico");

  const uploadFile = async (file: File) => {
    try {
      await runWithLoading(async () => {
        const url = await onUpload(file);
        setImage(url);
      });
    } catch (error) {
      reportError("Failed to upload image. Please try again.");
      console.error("Upload error:", error);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (isIcoFile(file)) {
      await uploadFile(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (readerEvent) => {
      const img = new Image();
      img.onload = async () => {
        if (
          maxWidth &&
          maxHeight &&
          (img.width > maxWidth || img.height > maxHeight)
        ) {
          reportError(
            `Image exceeds maximum dimensions of ${maxWidth}x${maxHeight} pixels.`
          );
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        } else if (
          minWidth &&
          minHeight &&
          (img.width < minWidth || img.height < minHeight)
        ) {
          reportError(
            `Image does not meet minimum dimensions of ${minWidth}x${minHeight} pixels.`
          );
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        await uploadFile(file);
      };
      img.onerror = () => {
        reportError("Could not read the selected image. Try PNG, JPG, SVG, or ICO.");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };
      img.src = readerEvent.target?.result as string;
    };
    reader.readAsDataURL(file);
  };
  const trimmedImage = image && String(image).trim();
  const previewSrc = trimmedImage || defaultImage;

  return (
    <div className={styles.section}>
      {label}
      <div className={styles.logoPreview}>
        <img
          src={previewSrc}
          alt={altText || ""}
          onError={event => {
            if (event.currentTarget.src !== defaultImage) {
              event.currentTarget.src = defaultImage;
            }
          }}
        />
      </div>
      <Button
        color="primary"
        outline
        size="sm"
        className={styles.changeButton}
        onClick={handleButtonClick}
        disabled={isUploading}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept={accept}
          onChange={handleImageChange}
          style={{ display: "none" }}
        />
        {isUploading ? (
          <><Spinner size="sm" /> Uploading...</>
        ) : (
          <>
            <FontAwesomeIcon icon={faRefresh} className="me-1" />
            Change image
          </>
        )}
      </Button>
    </div>
  );
};

export default withStyles(styles)(ImageSelectionInput);
